import { ReactNode, useEffect } from 'react'
import { useIntl } from 'react-intl'
import Box from 'components/shared/Box'
import NumberPicker from 'components/shared/NumberPicker'
import Button from 'components/shared/Button'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import { useAutoScroll } from 'utils/hooks'
import { useApp } from 'store/steps'
import { getDarkModeByLanguage } from 'helpers/theme'

interface QuantityPickerSectionProps {
    sectionHeader: ReactNode
    counterHeader?: ReactNode
    minValue: number
    maxValue: number
    value: number
    id?: string
    hasDarkMode?: boolean
    additionalInfo?: ReactNode
    testIdPrefix?: string
    onAdd: () => void
    onSubstract: () => void
    onSubmit: () => void
}

const QuantityPickerSection = ({
    sectionHeader,
    counterHeader,
    minValue,
    maxValue,
    value,
    id,
    hasDarkMode,
    additionalInfo,
    testIdPrefix,
    onAdd,
    onSubstract,
    onSubmit,
}: QuantityPickerSectionProps) => {
    const { formatMessage } = useIntl()
    const { animatePendingScroll, scrollIndex } = useAutoScroll()

    const { language } = useApp()

    useEffect(() => {
        id && animatePendingScroll(id)
    }, [scrollIndex])

    const theme = hasDarkMode ? getDarkModeByLanguage(language) : undefined
    const labelColor = hasDarkMode ? 'grayscale_0' : 'grayscale_900'
    const ctaVariant = hasDarkMode ? 'primary' : 'secondary'

    return (
        <Section id={id} theme={theme}>
            <Text
                variant="heading2"
                mb={additionalInfo ? 24 : 32}
                theme={theme}
            >
                {sectionHeader}
            </Text>

            {!!additionalInfo && <Box mb={24}>{additionalInfo}</Box>}

            <Box isFullWidth mb={[32, 40]}>
                {!!counterHeader && (
                    <Text variant="heading4" mb={[12, 16]} color={labelColor}>
                        {counterHeader}
                    </Text>
                )}
                <NumberPicker
                    minValue={minValue}
                    maxValue={maxValue}
                    value={value}
                    handleAdd={onAdd}
                    handleSubtract={onSubstract}
                    testIdPrefix={testIdPrefix}
                    theme={theme}
                />
            </Box>

            <Button
                variant={ctaVariant}
                hasArrow={false}
                width={['100%', '180px']}
                onClick={onSubmit}
                testId={
                    testIdPrefix ? `${testIdPrefix}-button-continue` : undefined
                }
            >
                {formatMessage({
                    id: 'global.btn.continue',
                    defaultMessage: 'Kontynuuj',
                })}
            </Button>
        </Section>
    )
}

export default QuantityPickerSection
